import { Link, Outlet, useNavigate } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { BarChart3, ClipboardList, LogOut, Receipt, Settings } from "lucide-react";
import { useTranslation } from "react-i18next";
import { cn } from "@/lib/cn";
import { Notice } from "@/components/ui/Field";
import { BillingNotice } from "@/features/billing/BillingNotice";
import { meQuery, useSignOut } from "@/features/auth/session";

const tabs = [
  { to: "/receipts", key: "nav.receipts", Icon: Receipt },
  { to: "/orders", key: "nav.orders", Icon: ClipboardList },
  { to: "/reports", key: "nav.reports", Icon: BarChart3 },
  { to: "/settings", key: "nav.settings", Icon: Settings },
] as const;

export function AppShell() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  // The guard has already loaded this, so it is served from the cache.
  const me = useQuery(meQuery);
  const signOut = useSignOut();

  return (
    <div className="mx-auto flex min-h-full max-w-screen-md flex-col">
      <header className="sticky top-0 z-10 flex items-center justify-between gap-3 border-b border-stone-200 bg-white/90 px-4 py-3 backdrop-blur">
        <span className="text-brand-700 text-lg font-semibold">{t("app.name")}</span>
        <div className="flex items-center gap-3">
          {me.data?.workspace && (
            <span className="max-w-40 truncate text-sm text-stone-600">
              {me.data.workspace.name}
            </span>
          )}
          <button
            type="button"
            aria-label={t("auth.signOut")}
            title={t("auth.signOut")}
            disabled={signOut.isPending}
            onClick={() => {
              signOut.mutate(undefined, {
                // The guard would also catch this, but navigating now avoids a render of a
                // screen the user is no longer entitled to.
                onSettled: () => void navigate({ to: "/auth/sign-in" }),
              });
            }}
            className="rounded-md p-2 text-stone-500 hover:bg-stone-100 hover:text-stone-800"
          >
            <LogOut className="h-5 w-5" aria-hidden />
          </button>
        </div>
      </header>
      <main className="flex-1 px-4 py-4 pb-24">
        {/* An account with no workspace can reach here — `/me` answers 200, so the route guard is
            satisfied — but every page below would 404, because each one resolves a workspace from
            the membership. Today only `createsuperuser` produces such an account, since sign-up
            always creates one; the deletion and downgrade paths in Milestone 7 could too. Say so
            plainly rather than letting someone wander into pages that cannot work. */}
        {me.data && !me.data.workspace ? (
          <Notice tone="info">{t("app.noWorkspace")}</Notice>
        ) : (
          <>
            {me.data?.workspace && <BillingNotice workspace={me.data.workspace} />}
            <Outlet />
          </>
        )}
      </main>
      <nav className="fixed inset-x-0 bottom-0 z-10 border-t border-stone-200 bg-white pb-[env(safe-area-inset-bottom)]">
        <ul className="mx-auto grid max-w-screen-md grid-cols-4">
          {tabs.map(({ to, key, Icon }) => (
            <li key={to}>
              <Link
                to={to}
                className={cn(
                  "flex flex-col items-center gap-1 py-2 text-xs text-stone-500",
                  "[&.active]:text-brand-700",
                )}
              >
                <Icon className="h-5 w-5" aria-hidden />
                {t(key)}
              </Link>
            </li>
          ))}
        </ul>
      </nav>
    </div>
  );
}
